import React from 'react';
import type { FormField } from '../../types';
import { validateFormFields } from '../../lib/form-capabilities';

/** Authored numeric scale displayed on the card without pretending to collect an answer. */
export function RatingFieldPreview({ field, onEdit }: {
  field: Extract<FormField, { type: 'number' }>;
  onEdit: () => void;
}): React.ReactElement {
  const min = field.min ?? 1;
  const max = field.max ?? 5;
  const step = field.step ?? 1;
  const count = Math.round((max - min) / step) + 1;
  const validScale = !validateFormFields([field], { allowEmptyLabels: true })
    .some(issue => issue.reason === 'invalid_rating' || issue.reason === 'invalid_number');
  const values = validScale && step > 0 && Number.isFinite(count) && count >= 2 && count <= 101
    ? Array.from({ length: count }, (_, index) => index === count - 1 ? max : min + index * step) : [];
  const descending = field.rating?.direction === 'descending';
  if (descending) values.reverse();
  if (!values.length) return <button type="button" onClick={onEdit}
    className="cursor-pointer rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt">Set scale</button>;
  return <div className="space-y-2">
    <ol aria-label="Rating scale" className="flex flex-wrap gap-1">
      {values.map(value => <li key={value} className="flex min-h-10 min-w-8 flex-1 items-center justify-center rounded-lg bg-surface-raised px-2 text-sm text-foreground">{value}</li>)}
    </ol>
    <div className="flex justify-between gap-4 text-sm leading-snug text-muted-foreground">
      <span className="max-w-[45%] break-words">{descending ? field.rating?.highLabel : field.rating?.lowLabel}</span>
      <span className="max-w-[45%] break-words text-right">{descending ? field.rating?.lowLabel : field.rating?.highLabel}</span>
    </div>
  </div>;
}
